import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, ClipboardList, Plus, Trash2, Settings, Check, Trophy,
  Flame, Star, Rocket, Medal, Crown, Sparkles, ChevronRight
} from "lucide-react";
import { API } from "../App";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

const BADGE_ICONS = [Rocket, Star, Medal, Crown, Sparkles, Trophy];
const BADGE_COLORS = [
  "from-blue-500 to-indigo-600",
  "from-violet-500 to-purple-600",
  "from-amber-500 to-orange-600",
  "from-emerald-500 to-green-600",
  "from-rose-500 to-pink-600",
  "from-yellow-400 to-amber-500",
];

const BADGE_LEVEL_I18N = {
  "Apprenti Astronaute": "parentTask.apprentice",
  "Pilote Spatial": "parentTask.pilot",
  "Commandant Galactique": "parentTask.commander",
  "Heros de l'Espace": "parentTask.hero",
  "Legende Cosmique": "parentTask.legend",
  "Maitre de l'Univers": "parentTask.master",
};
const translateBadgeName = (name, t) => BADGE_LEVEL_I18N[name] ? t(BADGE_LEVEL_I18N[name], name) : name;

const ParentTaskBoardPage = ({ user, logout }) => {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [tasks, setTasks] = useState([]);
  const [todayCompleted, setTodayCompleted] = useState([]);
  const [stats, setStats] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [newTask, setNewTask] = useState("");
  const [loading, setLoading] = useState(true);
  const [celebrating, setCelebrating] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [templateRes, todayRes, statsRes] = await Promise.all([
        axios.get(`${API}/parent-tasks/template`),
        axios.get(`${API}/parent-tasks/today`),
        axios.get(`${API}/parent-tasks/stats`),
      ]);
      setTasks(templateRes.data.tasks || []);
      setTodayCompleted(todayRes.data || []);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const addTask = () => {
    if (!newTask.trim()) return;
    const updated = [...tasks, newTask.trim()];
    setTasks(updated);
    setNewTask("");
    saveTemplate(updated);
  };

  const removeTask = (index) => {
    const updated = tasks.filter((_, i) => i !== index);
    setTasks(updated);
    saveTemplate(updated);
  };

  const saveTemplate = async (tasksList) => {
    try { await axios.put(`${API}/parent-tasks/template`, { tasks: tasksList }); }
    catch (err) { console.error(err); }
  };

  const isTaskDone = (taskName) => todayCompleted.some(c => c.task_name === taskName);

  const completeTask = async (taskName) => {
    if (isTaskDone(taskName)) return;
    try {
      await axios.post(`${API}/parent-tasks/complete`, { task_name: taskName });
      toast.success(t("parentTask.missionComplete"));

      // Reload data
      const [todayRes, statsRes] = await Promise.all([
        axios.get(`${API}/parent-tasks/today`),
        axios.get(`${API}/parent-tasks/stats`),
      ]);
      setTodayCompleted(todayRes.data || []);

      // Check for newly earned badge
      const oldBadgeCount = stats?.badges_earned?.length || 0;
      const newBadgeCount = statsRes.data?.badges_earned?.length || 0;
      if (newBadgeCount > oldBadgeCount) {
        const newBadge = statsRes.data.badges_earned[newBadgeCount - 1];
        setCelebrating(newBadge);
        setTimeout(() => setCelebrating(null), 4000);
      }
      setStats(statsRes.data);
    } catch (err) {
      toast.error(t("common.errorGeneric"));
      console.error(err);
    }
  };

  const todayDoneCount = todayCompleted.length;
  const totalTasks = tasks.length;
  const todayProgress = totalTasks > 0 ? Math.round((todayDoneCount / totalTasks) * 100) : 0;

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="space-y-4 max-w-2xl mx-auto">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="parent-task-board-page">
        {/* Badge celebration overlay */}
        {celebrating && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-in fade-in" data-testid="badge-celebration">
            <div className="bg-white dark:bg-zinc-900 rounded-2xl p-8 text-center shadow-2xl animate-in zoom-in max-w-sm mx-4">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center animate-bounce">
                <Trophy className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{t("parentTask.newBadge")}</h2>
              <p className="text-lg font-semibold text-amber-600 dark:text-amber-400">{celebrating.name}</p>
              <p className="text-sm text-muted-foreground mt-1">{celebrating.description}</p>
              <Button onClick={() => setCelebrating(null)} className="mt-4" variant="outline">{t("parentTask.great")}</Button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-indigo-500" />
              {t("parentTask.title")}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {t("parentTask.subtitle")}
            </p>
          </div>
          <Button variant="outline" size="icon" onClick={() => setShowSettings(!showSettings)} data-testid="settings-btn">
            <Settings className="w-4 h-4" />
          </Button>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-3 gap-3">
            <Card className="text-center">
              <CardContent className="p-3">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{stats.total_completions}</div>
                <div className="text-xs text-muted-foreground">{t("parentTask.totalMissions", "Missions totales")}</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="p-3">
                <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 flex items-center justify-center gap-1">
                  <Trophy className="w-5 h-5" />{stats.badges_earned?.length || 0}
                </div>
                <div className="text-xs text-muted-foreground">{t("parentTask.badgesEarned", "Badges gagnés")}</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="p-3">
                <div className="text-2xl font-bold text-red-500 flex items-center justify-center gap-1">
                  <Flame className="w-5 h-5" />{stats.streak}
                </div>
                <div className="text-xs text-muted-foreground">{t("parentTask.streak", "Jours de suite")}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Badge Progress */}
        {stats && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" /> {t("parentTask.missionBadges")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {stats.thresholds?.map((threshold, i) => {
                  const earned = stats.total_completions >= threshold.count;
                  const Icon = BADGE_ICONS[i % BADGE_ICONS.length];
                  return (
                    <div key={i} className="flex flex-col items-center" data-testid={`mission-badge-${i}`}>
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                        earned
                          ? `bg-gradient-to-br ${BADGE_COLORS[i % BADGE_COLORS.length]} text-white shadow-lg`
                          : "bg-muted text-muted-foreground/40"
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className="text-[10px] font-medium text-center mt-1.5 leading-tight">{translateBadgeName(threshold.name, t)}</span>
                      <span className="text-[9px] text-muted-foreground">{threshold.count} {t("parentTask.missions")}</span>
                    </div>
                  );
                })}
              </div>
              {stats.next_badge && (
                <div className="mt-4 pt-3 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t("parentTask.nextBadge", "Prochain badge")} : <strong>{translateBadgeName(stats.next_badge.name, t)}</strong></span>
                    <span className="font-medium">{stats.total_completions}/{stats.next_badge.count}</span>
                  </div>
                  <div className="w-full h-2.5 bg-muted rounded-full mt-2 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (stats.total_completions / stats.next_badge.count) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Today's Progress */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{t("parentTask.todayMissions", "Missions du jour")}</CardTitle>
              <span className="text-sm font-medium text-muted-foreground">{todayDoneCount}/{totalTasks}</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden mt-2">
              <div
                className={`h-full rounded-full transition-all duration-500 ${todayProgress >= 100 ? "bg-emerald-500" : "bg-indigo-500"}`}
                style={{ width: `${todayProgress}%` }}
                data-testid="today-progress"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {tasks.map((task, i) => {
              const done = isTaskDone(task);
              return (
                <div
                  key={i}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    done
                      ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800"
                      : "hover:bg-muted/50 cursor-pointer"
                  }`}
                  onClick={() => !done && completeTask(task)}
                  data-testid={`task-${i}`}
                >
                  <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    done ? "bg-emerald-500 border-emerald-500 text-white" : "border-muted-foreground/30"
                  }`}>
                    {done && <Check className="w-4 h-4" />}
                  </div>
                  <span className={`flex-1 text-sm ${done ? "line-through text-muted-foreground" : "font-medium"}`}>{task}</span>
                  {done && <Star className="w-4 h-4 text-amber-400" />}
                  {showSettings && !done && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); removeTask(i); }} data-testid={`remove-task-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}

            {showSettings && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                <Input
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  placeholder={t("parentTask.addMission")}
                  className="flex-1"
                  onKeyDown={(e) => e.key === "Enter" && addTask()}
                  data-testid="new-task-input"
                />
                <Button size="icon" onClick={addTask} disabled={!newTask.trim()} data-testid="add-task-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Weekly Heatmap */}
        {stats?.week_data && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t("parentTask.thisWeek")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2">
                {stats.week_data.map((day, i) => {
                  const dayName = new Date(day.date + "T12:00:00").toLocaleDateString(locale === "es" ? "es-ES" : locale === "en" ? "en-US" : "fr-FR", { weekday: "short" });
                  return (
                    <div key={i} className="text-center" data-testid={`week-day-${i}`}>
                      <div className="text-[10px] text-muted-foreground mb-1">{dayName}</div>
                      <div className={`w-full aspect-square rounded-lg flex items-center justify-center text-sm font-bold ${
                        day.count >= 5 ? "bg-emerald-500 text-white" :
                        day.count >= 3 ? "bg-emerald-300 dark:bg-emerald-700 text-emerald-900 dark:text-emerald-100" :
                        day.count >= 1 ? "bg-emerald-100 dark:bg-emerald-900 text-emerald-600" :
                        "bg-muted text-muted-foreground"
                      }`}>
                        {day.count}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {todayProgress >= 100 && (
          <div className="text-center py-4">
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5" /> {t("parentTask.allDone")} <Sparkles className="w-5 h-5" />
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ParentTaskBoardPage;
